import React, { useState } from 'react';
import { useResearchDemo } from '../../contexts/ResearchDemoContext';
import type { Claim, ReviewOutcome } from '../../types/researchDemo';
import { errorClass, fieldClass, glassPanel, labelClass, primaryButton } from '../../utils/demoUi';
import { defaultReviewDraft, outcomeLabels, validateReview } from '../../utils/researchDemo';
const outcomes: ReviewOutcome[] = ['approved', 'revision-requested', 'rejected'];
export function ReviewForm({
  claim
}: {
  claim: Claim;
}) {
  const {
    state,
    dispatch
  } = useResearchDemo();
  const draft = state.ui.reviewDrafts[claim.id] ?? defaultReviewDraft(claim);
  const [attempted, setAttempted] = useState(false);
  const [message, setMessage] = useState('');
  const errors = attempted ? validateReview(draft) : {};
  const update = (patch: Partial<typeof draft>) => {
    setMessage('');
    dispatch({
      type: 'setReviewDraft',
      claimId: claim.id,
      draft: patch
    });
  };
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (Object.keys(validateReview(draft)).length > 0 || !draft.outcome) return;
    dispatch({
      type: 'submitReview',
      claimId: claim.id
    });
    setAttempted(false);
    setMessage(`Review recorded: ${outcomeLabels[draft.outcome]}. ${claim.id} is now version ${claim.version + 1}; version ${claim.version} is preserved below.`);
  };
  return <form onSubmit={submit} noValidate aria-labelledby="review-form-title" className={`${glassPanel} p-6`} data-id="element-2282" data-mp="1uicv7jfqxhyv">
      <h2 id="review-form-title" className="font-heading text-lg font-bold text-ink-dark" data-id="element-2283" data-mp="1pizx22jfcogz">
        Record a review
      </h2>
      <p className="mt-1 text-sm text-ink-muted" data-id="element-2284" data-mp="1fmwz78vlvbzu">Every review creates a new version. The current version is preserved in the history.</p>

      <fieldset className="mt-5" aria-describedby={errors.outcome ? 'outcome-error' : undefined} data-id="element-2285" data-mp="1bmwg3axkacmg">
        <legend className={labelClass} data-id="element-2286" data-mp="39zwknimo30f1">Outcome</legend>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3" data-id="element-2287" data-mp="35zvskv6naqpi">
          {outcomes.map(outcome => <label key={outcome} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors duration-150 ${draft.outcome === outcome ? 'border-lavender bg-lavender-pale text-ink-dark' : 'border-line bg-white text-ink-dark'}`} data-id="element-2288" data-mp="13cfbc56wx1qr">
              <input type="radio" name={`outcome-${claim.id}`} value={outcome} checked={draft.outcome === outcome} onChange={() => update({
            outcome
          })} className="h-4 w-4 accent-[#6366C9]" data-id="element-2289" data-mp="ybrjxieruiw0" />
              {outcomeLabels[outcome]}
            </label>)}
        </div>
        {errors.outcome && <p id="outcome-error" className={errorClass} data-id="element-2290" data-mp="1nle4s645kg5">
            {errors.outcome}
          </p>}
      </fieldset>

      <div className="mt-5" data-id="element-2291" data-mp="6myaq4mse5vx">
        <label htmlFor={`interp-${claim.id}`} className={labelClass} data-id="element-2292" data-mp="6o9ei4g8fdn1">
          Interpretation
        </label>
        <textarea id={`interp-${claim.id}`} rows={3} value={draft.interpretation} onChange={event => update({
        interpretation: event.target.value
      })} aria-invalid={Boolean(errors.interpretation)} className={fieldClass} data-id="element-2293" data-mp="3u98pitjts7ks" />
        {errors.interpretation && <p className={errorClass} data-id="element-2294" data-mp="32xt5aprfuzs">{errors.interpretation}</p>}
      </div>
      <div className="mt-4" data-id="element-2295" data-mp="3bl3nprmqawom">
        <label htmlFor={`limit-${claim.id}`} className={labelClass} data-id="element-2296" data-mp="3bi7p013243z4">
          Scope and limitations
        </label>
        <textarea id={`limit-${claim.id}`} rows={3} value={draft.limitation} onChange={event => update({
        limitation: event.target.value
      })} aria-invalid={Boolean(errors.limitation)} className={fieldClass} data-id="element-2297" data-mp="3la3rrpg38i9o" />
        {errors.limitation && <p className={errorClass} data-id="element-2298" data-mp="2nv9cfei25g6h">{errors.limitation}</p>}
      </div>

      <div className="mt-4" data-id="element-2299" data-mp="2nsvjpcauog23">
        <label htmlFor={`rationale-${claim.id}`} className={labelClass} data-id="element-2300" data-mp="1r3utkfbst9tz">
          Rationale
        </label>
        <textarea id={`rationale-${claim.id}`} rows={3} value={draft.rationale} onChange={event => update({
        rationale: event.target.value
      })} aria-invalid={Boolean(errors.rationale)} aria-describedby={errors.rationale ? `rationale-error-${claim.id}` : undefined} className={fieldClass} data-id="element-2301" data-mp="1w263eq23fkc7" />
        {errors.rationale && <p id={`rationale-error-${claim.id}`} className={errorClass} data-id="element-2302" data-mp="2esnpibsf7hol">
            {errors.rationale}
          </p>}
      </div>

      <fieldset className="mt-5 rounded-xl border border-line bg-surface-bg p-4" data-id="element-2303" data-mp="2jsry7fn37849">
        <legend className="px-1 text-sm font-semibold text-ink-dark" data-id="element-2304" data-mp="2nskb24jik0ff">Evidence checks · required before approval</legend>
        <label className="mt-1 flex items-start gap-3 text-sm text-ink-dark" data-id="element-2305" data-mp="pg25304jm2xx">
          <input type="checkbox" checked={draft.checkedPassage} onChange={event => update({
          checkedPassage: event.target.checked
        })} className="mt-0.5 h-4 w-4 accent-[#6366C9]" data-id="element-2306" data-mp="tglzvx1n8ugw" />
          I inspected the supporting passage.
        </label>
        <label className="mt-2 flex items-start gap-3 text-sm text-ink-dark" data-id="element-2307" data-mp="c7be49a5d1sr">
          <input type="checkbox" checked={draft.checkedLimits} onChange={event => update({
          checkedLimits: event.target.checked
        })} className="mt-0.5 h-4 w-4 accent-[#6366C9]" data-id="element-2308" data-mp="85pu83z73n92" />
          I checked that the interpretation retains the relevant limitations.
        </label>
        {errors.checks && <p className={errorClass} data-id="element-2309" data-mp="vwlulphpvqrx">{errors.checks}</p>}
      </fieldset>

      <div className="mt-6 flex flex-wrap items-center gap-4" data-id="element-2310" data-mp="qumqgde5kptx">
        <button type="submit" className={primaryButton} data-id="element-2311" data-mp="3gkgia6gk2o83">
          Save review
        </button>
        <p role="status" className="text-sm font-medium text-navy" data-id="element-2312" data-mp="3blwwq75vjeg9">
          {message}
        </p>
      </div>
    </form>;
}